'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { NextIntlClientProvider, useTranslations, useLocale } from 'next-intl';
import { useRouter } from '@/i18n/routing';
import Image from 'next/image';

type ChatMessage = { role: 'bot' | 'user'; text: string };

type Props = {
  locale: string;
  messages: Parameters<typeof NextIntlClientProvider>[0]['messages'];
};

export default function Portfolio({ locale, messages }: Props) {
  return (
    <NextIntlClientProvider locale={locale} messages={messages} timeZone="America/Sao_Paulo">
      <PortfolioContent />
    </NextIntlClientProvider>
  );
}

function PortfolioContent() {
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();

  const switchLocale = useCallback((lang: 'pt' | 'en' | 'es') => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    router.push('/', { locale: lang });
  }, [router]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const chatMessagesRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>(() => [
    { role: 'bot', text: t('chat_intro') }
  ]);
  const [chatInput, setChatInput] = useState('');

  // Custom cursor
  useEffect(() => {
    const cursor = cursorRef.current;
    if (!cursor) return;
    const onMove = (e: MouseEvent) => {
      cursor.style.left = e.clientX + 'px';
      cursor.style.top = e.clientY + 'px';
    };
    document.addEventListener('mousemove', onMove);
    const onEnter = () => cursor.classList.add('expand');
    const onLeave = () => cursor.classList.remove('expand');
    document.querySelectorAll('a, button, input').forEach(el => {
      el.addEventListener('mouseenter', onEnter);
      el.addEventListener('mouseleave', onLeave);
    });
    return () => document.removeEventListener('mousemove', onMove);
  }, []);

  // Particle hero — deferred past first paint to keep the main thread free
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let stop: (() => void) | undefined;
    let cancelled = false;

    const start = () => {
      if (cancelled) return;
      import('./heroParticles').then(m => {
        if (!cancelled) stop = m.startHeroParticles(canvas);
      });
    };
    const hasIdle = 'requestIdleCallback' in window;
    const idleId = hasIdle
      ? window.requestIdleCallback(start, { timeout: 2500 })
      : window.setTimeout(start, 1200);

    return () => {
      cancelled = true;
      if (hasIdle) window.cancelIdleCallback(idleId);
      else window.clearTimeout(idleId);
      stop?.();
    };
  }, []);

  // Scroll reveal + skill bars (IntersectionObserver instead of GSAP)
  useEffect(() => {
    const reveal = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          reveal.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -18% 0px' });
    document.querySelectorAll('.reveal').forEach(el => reveal.observe(el));

    const chart = document.getElementById('bar-chart');
    const bars = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      document.querySelectorAll<HTMLElement>('.bar-fill').forEach(b => {
        b.style.width = (b.dataset.w ?? '0') + '%';
      });
      bars.disconnect();
    }, { rootMargin: '0px 0px -20% 0px' });
    if (chart) bars.observe(chart);

    return () => { reveal.disconnect(); bars.disconnect(); };
  }, []);

  // Scroll chat to bottom
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const getOfflineResponse = useCallback((q: string): string => {
    const lq = q.toLowerCase();
    if (lq.includes('flutter') || lq.includes('dart')) {
      return locale === 'en'
        ? 'Rodrigo is a Flutter and Dart specialist with 4+ years of experience, including production apps at Ambev Tech.'
        : locale === 'es'
        ? 'Rodrigo es especialista en Flutter y Dart con más de 4 años de experiencia, incluyendo apps en producción en Ambev Tech.'
        : 'Rodrigo é especialista em Flutter e Dart com 4+ anos de experiência, incluindo apps em produção na Ambev Tech.';
    }
    if (lq.includes('experiência') || lq.includes('experience') || lq.includes('experiencia') || lq.includes('ambev')) {
      return locale === 'en'
        ? 'Rodrigo has 4+ years as a Flutter Developer, currently at Ambev Tech (since 2021). Previously at TalkProcess and as a freelancer.'
        : locale === 'es'
        ? 'Rodrigo tiene 4+ años como Flutter Developer, actualmente en Ambev Tech (desde 2021). Antes en TalkProcess y como freelancer.'
        : 'Rodrigo tem 4+ anos como Flutter Developer, atualmente na Ambev Tech (desde 2021). Antes na TalkProcess e como freelancer.';
    }
    if (lq.includes('projeto') || lq.includes('project') || lq.includes('app')) {
      return locale === 'en'
        ? 'Key projects: Logistics App at Ambev Tech (in production), and freelance: SaúdeWeb, Kduda (multi-platform), and Endocontrol (health app).'
        : locale === 'es'
        ? 'Proyectos: App Logístico en Ambev Tech (en producción), y freelance: SaúdeWeb, Kduda (multi-plataforma) y Endocontrol (salud).'
        : 'Projetos: App Logístico na Ambev Tech (em produção), e freelance: SaúdeWeb, Kduda (multi-plataforma) e Endocontrol (saúde).';
    }
    if (lq.includes('contato') || lq.includes('contact') || lq.includes('email') || lq.includes('linkedin')) {
      return locale === 'en'
        ? 'Reach Rodrigo at contato@rodrigopequeno.dev or LinkedIn: linkedin.com/in/rodrigopequeno'
        : locale === 'es'
        ? 'Contacta en contato@rodrigopequeno.dev o LinkedIn: linkedin.com/in/rodrigopequeno'
        : 'Contato: contato@rodrigopequeno.dev ou LinkedIn: linkedin.com/in/rodrigopequeno';
    }
    if (lq.includes('skill') || lq.includes('tecnolog') || lq.includes('stack')) {
      return 'Flutter, Dart, Clean Architecture, SOLID, REST APIs, Firebase, CI/CD (Codemagic/TestFlight), Datadog RUM, SignalR, SQLite.';
    }
    return t('chat_fallback');
  }, [locale, t]);

  const sendMessage = useCallback(() => {
    const q = chatInput.trim();
    if (!q) return;
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', text: q }]);
    setTimeout(() => {
      setChatMessages(prev => [...prev, { role: 'bot', text: getOfflineResponse(q) }]);
    }, 600);
  }, [chatInput, getOfflineResponse]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') sendMessage();
  };

  return (
    <>
      <div ref={cursorRef} id="cursor" />

      <nav>
        <a href="#" className="nav-logo">RP</a>
        <div className="nav-right">
          <div className="nav-links">
            <a href="#identity">{t('nav_identity')}</a>
            <a href="#workshop">{t('nav_workshop')}</a>
            <a href="#lab">{t('nav_lab')}</a>
            <a href="#lens">{t('nav_lens')}</a>
            <a href="#contact">{t('nav_contact')}</a>
          </div>
          <div className="lang-switcher">
            <button onClick={() => switchLocale('pt')} className={`lang-btn${locale === 'pt' ? ' active' : ''}`} lang="pt" aria-pressed={locale === 'pt'}>PT</button>
            <button onClick={() => switchLocale('en')} className={`lang-btn${locale === 'en' ? ' active' : ''}`} lang="en" aria-pressed={locale === 'en'}>EN</button>
            <button onClick={() => switchLocale('es')} className={`lang-btn${locale === 'es' ? ' active' : ''}`} lang="es" aria-pressed={locale === 'es'}>ES</button>
          </div>
          <button className={`hamburger${menuOpen ? ' open' : ''}`} onClick={() => setMenuOpen(o => !o)} aria-label="Menu" aria-expanded={menuOpen}>
            <span /><span /><span />
          </button>
        </div>
        {menuOpen && (
          <div className="nav-mobile">
            <a href="#identity" onClick={() => setMenuOpen(false)}>{t('nav_identity')}</a>
            <a href="#workshop" onClick={() => setMenuOpen(false)}>{t('nav_workshop')}</a>
            <a href="#lab" onClick={() => setMenuOpen(false)}>{t('nav_lab')}</a>
            <a href="#lens" onClick={() => setMenuOpen(false)}>{t('nav_lens')}</a>
            <a href="#contact" onClick={() => setMenuOpen(false)}>{t('nav_contact')}</a>
          </div>
        )}
      </nav>

      <main>
      <section id="hero">
        <canvas ref={canvasRef} id="three-canvas" />
        <div className="hero-content">
          <div className="hero-label">{t('hero_label')}</div>
          <h1 className="hero-name">
            <span className="line">Rodrigo</span>
            <span className="line">Pequeno.</span>
          </h1>
          <p className="hero-role">
            {t('hero_intro')} <span>{t('hero_span')}</span> — <span>{t('hero_role_text')}</span>
          </p>
          <div className="hero-scroll">
            <div className="scroll-line" />
            <span>{t('hero_scroll')}</span>
          </div>
        </div>
      </section>

      <section id="identity" className="chapter">
        <div className="chapter-label">{t('ch1_label')}</div>
        <h2 className="chapter-title reveal">{t('ch1_title')}</h2>
        <div className="bento bento-identity reveal">
          <div className="cell cell-avatar">
            <Image
              src="/images/avatar.webp"
              alt="Rodrigo Pequeno"
              width={520}
              height={720}
              sizes="(max-width: 768px) 100vw, 260px"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }}
              priority
            />
          </div>
          <div className="cell cell-bio">
            <p className="bio-text">{t.rich('bio1', { strong: (c) => <strong>{c}</strong> })}</p>
            <p className="bio-text">{t.rich('bio2', { strong: (c) => <strong>{c}</strong> })}</p>
            <p className="bio-text">{t.rich('bio3', { strong: (c) => <strong>{c}</strong> })}</p>
            <a href="mailto:contato@rodrigopequeno.dev" className="bio-cta">
              contato@rodrigopequeno.dev
            </a>
          </div>
          <div className="cell cell-location">
            <div className="label">{t('loc_label')}</div>
            <div className="value">{t('loc_line1')}<br />{t('loc_line2')}</div>
          </div>
          <div className="cell cell-experience">
            <div className="num">4+</div>
            <div className="desc">{t('exp_line1')}<br />{t('exp_line2')}</div>
          </div>
          <div className="cell cell-ambev">
            <div className="ambev-dot" />
            <div className="ambev-info">
              <div className="company">Ambev Tech</div>
              <div className="role">{t('ambev_role')}</div>
            </div>
          </div>
        </div>
      </section>

      <section id="workshop" className="chapter">
        <div className="chapter-label">{t('ch2_label')}</div>
        <h2 className="chapter-title reveal">{t('ch2_title')}</h2>
        <div className="bento bento-workshop reveal">
          <div className="skill-cell">
            <div className="skill-icon">◆</div>
            <div><div className="skill-name">Flutter</div><div className="skill-desc">PRIMARY STACK</div></div>
          </div>
          <div className="skill-cell">
            <div className="skill-icon">▲</div>
            <div><div className="skill-name">Dart</div><div className="skill-desc">LANGUAGE</div></div>
          </div>
          <div className="skill-cell">
            <div className="skill-icon">●</div>
            <div><div className="skill-name">Firebase</div><div className="skill-desc">BACKEND</div></div>
          </div>
          <div className="skill-cell">
            <div className="skill-icon">■</div>
            <div><div className="skill-name">REST APIs</div><div className="skill-desc">INTEGRATION</div></div>
          </div>
          <div className="skill-cell wide" style={{ background: 'var(--s2)', padding: '28px 32px' }}>
            <div className="skill-name" style={{ fontSize: '20px', marginBottom: '16px' }}>{t('bar_title')}</div>
            <div className="bar-chart" id="bar-chart">
              {([['Flutter/Dart', 95], ['Clean Arch.', 90], ['REST / APIs', 88], ['Firebase', 82], ['CI/CD', 78], ['C#/.NET', 55]] as [string, number][]).map(([label, w]) => (
                <div className="bar-row" key={label}>
                  <div className="bar-label">{label}</div>
                  <div className="bar-track"><div className="bar-fill" data-w={w} /></div>
                </div>
              ))}
            </div>
          </div>
          <div className="skill-cell">
            <div className="skill-icon">⬡</div>
            <div><div className="skill-name">CI/CD</div><div className="skill-desc">DEVOPS</div></div>
            <div className="skill-tags">
              <span className="skill-tag">TestFlight</span>
              <span className="skill-tag">Play Console</span>
              <span className="skill-tag">Codemagic</span>
            </div>
          </div>
          <div className="skill-cell">
            <div className="skill-icon">◎</div>
            <div><div className="skill-name">Datadog</div><div className="skill-desc">MONITORING</div></div>
          </div>
          <div className="skill-cell" style={{ background: 'var(--s2)' }}>
            <div><div className="skill-name">Princípios</div><div className="skill-desc" style={{ marginBottom: '10px' }}>SOLID · DRY · KISS</div></div>
            <div className="skill-tags">
              <span className="skill-tag">OWASP</span>
              <span className="skill-tag">i18n</span>
              <span className="skill-tag">SignalR</span>
              <span className="skill-tag">SQLite</span>
            </div>
          </div>
        </div>
      </section>

      <section id="lab" className="chapter">
        <div className="chapter-label">{t('ch3_label')}</div>
        <h2 className="chapter-title reveal">{t('ch3_title')}</h2>
        <div className="bento bento-lab reveal">
          {([
            { num: 'proj1_num', title: 'proj1_title', desc: 'proj1_desc', impact: 'proj1_impact', tags: ['Flutter', 'Clean Architecture', 'Datadog', 'SignalR', 'CI/CD'] },
            { num: 'proj2_num', title: 'proj2_title', desc: 'proj2_desc', impact: 'proj2_impact', tags: ['Flutter', 'Android Native', 'Bridge', 'Engenharia Reversa'] },
            { num: 'proj3_num', title: 'proj3_title', desc: 'proj3_desc', impact: 'proj3_impact', tags: ['Flutter', 'Flutter Web', 'REST API', 'Multi-plataforma'] },
            { num: 'proj4_num', title: 'proj4_title', desc: 'proj4_desc', impact: 'proj4_impact', tags: ['Flutter', 'Firebase', 'Health Tech'] },
          ] as const).map(p => (
            <div className="project-cell" key={p.num}>
              <div className="project-num">{t(p.num)}</div>
              <div className="project-title">{t(p.title)}</div>
              <div className="project-desc">{t(p.desc)}</div>
              <div className="project-tags">{p.tags.map(tag => <span key={tag} className="project-tag">{tag}</span>)}</div>
              <div className="project-impact">{t(p.impact)}</div>
            </div>
          ))}
        </div>
      </section>

      <section id="lens" className="chapter">
        <div className="chapter-label">{t('ch4_label')}</div>
        <h2 className="chapter-title reveal">{t('ch4_title')}</h2>
        <div className="bento bento-lens reveal">
          <div className="cell cell-quote">
            <div className="quote-mark">&ldquo;</div>
            <div className="quote-text">{t('quote')}</div>
          </div>
          <div className="cell cell-timeline">
            <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px' }}>{t('traj_title')}</div>
            {[
              { year: '2021→', title: 'Ambev Tech',                sub: 'FLUTTER DEVELOPER · PRESENTE' },
              { year: '2021',  title: 'TalkProcess',               sub: 'MOBILE FLUTTER DEVELOPER' },
              { year: '2020',  title: 'Freelance',                 sub: '4 APPS PUBLICADOS' },
              { year: '2020',  title: 'UFS — Ciência da Computação', sub: 'BACHARELADO' },
            ].map(item => (
              <div className="timeline-item" key={item.title}>
                <div className="t-year">{item.year}</div>
                <div className="t-content">
                  <div className="t-title">{item.title}</div>
                  <div className="t-sub">{item.sub}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="cell cell-values">
            <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px' }}>{t('val_title')}</div>
            {([
              { pt: 'Crescimento técnico contínuo',    en: 'Continuous technical growth',         es: 'Crecimiento técnico continuo',    icon: '↑' },
              { pt: 'Engenharia limpa e sustentável',  en: 'Clean and sustainable engineering',   es: 'Ingeniería limpia y sostenible',   icon: '◆' },
              { pt: 'UX que realmente importa',        en: 'UX that truly matters',               es: 'UX que realmente importa',         icon: '●' },
              { pt: 'Clareza técnica nas decisões',    en: 'Technical clarity in decisions',      es: 'Claridad técnica en decisiones',   icon: '▲' },
              { pt: 'Impacto real no usuário final',   en: 'Real impact on end users',            es: 'Impacto real en el usuario final', icon: '■' },
            ] as const).map(item => (
              <div className="value-item" key={item.icon}>
                <div className="v-label">{item[locale as 'pt' | 'en' | 'es'] ?? item.pt}</div>
                <div className="v-icon">{item.icon}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact">
        <h2 className="contact-title reveal">
          {t('contact_line1')}<br />
          <span className="accent">{t('contact_accent')}</span><br />
          {t('contact_line3')}
        </h2>
        <div className="contact-links reveal">
          <a href="mailto:contato@rodrigopequeno.dev" className="contact-link">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 7L2 7" />
            </svg>
            contato@rodrigopequeno.dev
          </a>
          <a href="https://linkedin.com/in/rodrigopequeno" target="_blank" rel="noopener noreferrer" className="contact-link">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
              <rect x="2" y="9" width="4" height="12" /><circle cx="4" cy="4" r="2" />
            </svg>
            LinkedIn
          </a>
        </div>
      </section>

      </main>

      <footer>{t('footer')}</footer>

      <button id="chat-fab" title={t('chat_name')} aria-label={t('chat_name')} aria-expanded={chatOpen} onClick={() => setChatOpen(o => !o)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      </button>

      <div id="chat-panel" className={chatOpen ? 'open' : ''}>
        <div className="chat-header">
          <div className="chat-avatar">RP</div>
          <div>
            <div className="chat-name">{t('chat_name')}</div>
            <div className="chat-status">● ONLINE</div>
          </div>
        </div>
        <div id="chat-messages" ref={chatMessagesRef}>
          {chatMessages.map((msg, i) => (
            <div key={i} className={`msg ${msg.role}`}>{msg.text}</div>
          ))}
        </div>
        <div className="chat-input-row">
          <input
            id="chat-input"
            type="text"
            placeholder={t('chat_placeholder')}
            maxLength={200}
            value={chatInput}
            onChange={e => setChatInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button id="chat-send" onClick={sendMessage} aria-label={t('chat_send')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
}
